import configJSON from 'config.json';
import { CountryCodeList } from 'types/countries';
import { SiteLanguage } from 'constants/locale';
import ExprealtyCaTheme from './theme-config';
import { createHomeConfig } from './home-config';
import { createJoinExpConfig } from './join-exp-config';
import {
  EXP_REALTY_CA_TENANT_ID,
  EXP_REALTY_CA_TENANT_ID_TYPE,
  ContentHosts,
  DEFAULT_GTM_CONTAINER_ID_KEY,
} from '@tenants/types';
import { createFooterConfig } from './footer-config';
import { Namespaces } from 'types/locales';

import type {
  HomeConfig,
  JoinExpConfig,
  Tenant,
  TenantConfigHome,
  TenantConfigJoinExp,
  FooterConfig,
  TenantConfigFooter,
  TenantConfigFilters,
} from '@tenants/types';

//#region Public Constants
export const EXP_REALTY_CA_L18N_NAMESPACES = [
  Namespaces.EXP_REALTY_CA,
  Namespaces.JOIN_EXP_CA,
  Namespaces.COMMON,
  Namespaces.FORM_ERROR,
  Namespaces.MODAL,
] as const;

export const JOIN_EXP_CA_GTM_CONTAINER_ID_KEY = 'JoinExpCa' as const;
//#endregion

//#region Public Types
export type ExpRealtyCaTenantConfig = TenantConfigHome & TenantConfigJoinExp & TenantConfigFooter & TenantConfigFilters;
export type ExpRealtyCaTenant = Tenant<EXP_REALTY_CA_TENANT_ID_TYPE, typeof ExprealtyCaTheme, ExpRealtyCaTenantConfig, typeof JOIN_EXP_CA_GTM_CONTAINER_ID_KEY>;
//#endregion

//#region Config Factories
const home: HomeConfig = createHomeConfig();
const joinExp: JoinExpConfig = createJoinExpConfig();
const footer: FooterConfig = createFooterConfig();
//#endregion

//#region Tenant Definition
export const expRealtyCATenant: ExpRealtyCaTenant = {
  id: EXP_REALTY_CA_TENANT_ID,
  hosts: configJSON.hosts[EXP_REALTY_CA_TENANT_ID],
  contentHosts: configJSON.contentPageApi[EXP_REALTY_CA_TENANT_ID] as ContentHosts,
  theme: ExprealtyCaTheme,
  localization: {
    languages: [SiteLanguage.English, SiteLanguage.French],
    countries: [CountryCodeList.CANADA],
    namespaces: EXP_REALTY_CA_L18N_NAMESPACES,
    defaultLanguage: SiteLanguage.English,
    defaultCountry: CountryCodeList.CANADA,
  },
  config: {
    home,
    joinExp,
    footer,
    filters: {
      countryCode: CountryCodeList.CANADA,
    },
  },
  routes: {
    claimed: '*',
  },
  debug: {
    forceTenantHeaderValue: `useTenant="${EXP_REALTY_CA_TENANT_ID}"`,
    forceTenantFeatureValue: EXP_REALTY_CA_TENANT_ID,
  },
  features: {
    MyLinkMyLead: false,
    AgentSearch: true,
    AccessibilityWidget: true,
    TrustArc: {
      enabled: true,
      domain: 'exprealty.com',
    },
  },
  googleTagManager: {
    containerIds: {
      [DEFAULT_GTM_CONTAINER_ID_KEY]: configJSON.googleTagManager.containerIdExpCa,
      [JOIN_EXP_CA_GTM_CONTAINER_ID_KEY]: configJSON.googleTagManager.containerIdJoinExp,
    },
  },
  sso: {
    googleWebClientId: process.env.NEXT_PUBLIC_GOOGLE_WEB_CLIENT_ID_EXP || '',
    facebookAppId: process.env.NEXT_PUBLIC_FACEBOOK_APP_ID_EXP || '',
  },
  sitemap: {
    url: [
      'https://www.exprealty.ca/sitemaps/area-pages/sitemap.xml',
      'https://www.exprealty.ca/sitemaps/address-pages/sitemap.xml',
      'https://www.exprealty.ca/sitemaps/root-pages/sitemap-index.xml',
    ],
  },
  pvr: {
    phoneNumber: '1-844-248-2174',
  },
};
//#endregion